import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Navbar } from './Navbar';
import { Sidebar } from './Sidebar';
import { TopNavBar } from './TopNavBar';
import { TrialBanner } from '../TrialBanner';
import { EmailVerificationBanner } from '../EmailVerificationBanner';
import { FeedbackModal } from '../FeedbackModal';
import { useAuth } from '../../hooks/useAuth';
import { loadSettings, applySettings } from '../../hooks/useApplySettings';
import '../../styles/layout.css';

const NO_BACK_ROUTES = ['/', '/dashboard', '/login'];
// Session pages manage their own back navigation via SessionHeaderBar
const NO_BACK_PREFIXES = ['/session'];

// Module-level flag so the daily check only fires once per page load, not on every re-render
let _dailyFeedbackChecked = false;

export const Layout = ({ children }) => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { currentUser } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [navMode, setNavMode] = useState(
    () => localStorage.getItem('sc-nav-mode') || 'sidebar'
  );
  const [showFeedback, setShowFeedback] = useState(false);
  const timerRef = useRef(null);

  // Re-apply settings whenever localStorage changes (e.g. user edits settings in another tab)
  useEffect(() => {
    applySettings(loadSettings());
    const handler = () => applySettings(loadSettings());
    window.addEventListener('storage', handler);
    return () => window.removeEventListener('storage', handler);
  }, []);

  // Daily 2pm feedback prompt
  useEffect(() => {
    if (_dailyFeedbackChecked || !currentUser) return;
    _dailyFeedbackChecked = true;

    const schedule = () => {
      const now = new Date();
      const lastShown = localStorage.getItem('sc-feedback-last-shown');
      const today = now.toDateString();

      if (lastShown === today) return; // already shown today

      const twopm = new Date(now);
      twopm.setHours(14, 0, 0, 0);

      const msUntil2pm = twopm - now;

      if (msUntil2pm <= 0) {
        // Already past 2pm — show now
        localStorage.setItem('sc-feedback-last-shown', today);
        setShowFeedback(true);
      } else {
        // Schedule for 2pm
        timerRef.current = setTimeout(() => {
          localStorage.setItem('sc-feedback-last-shown', new Date().toDateString());
          setShowFeedback(true);
        }, msUntil2pm);
      }
    };

    schedule();
    return () => clearTimeout(timerRef.current);
  }, [currentUser]);

  const showBack = !NO_BACK_ROUTES.includes(pathname) &&
    !NO_BACK_PREFIXES.some(p => pathname.startsWith(p));

  const toggleNavMode = () => {
    const next = navMode === 'sidebar' ? 'topnav' : 'sidebar';
    localStorage.setItem('sc-nav-mode', next);
    setNavMode(next);
    setSidebarOpen(false);
  };

  const backButton = showBack ? (
    <button className="layout-back-btn" onClick={() => navigate(-1)}>
      ← Back
    </button>
  ) : null;

  const feedbackModal = showFeedback ? (
    <FeedbackModal onClose={() => setShowFeedback(false)} />
  ) : null;

  if (navMode === 'topnav') {
    return (
      <div className="app-layout topnav-mode">
        <TrialBanner />
        <EmailVerificationBanner />
        <TopNavBar onToggleNavMode={toggleNavMode} />
        <main className="main-content topnav-content">
          {backButton}
          {children}
        </main>
        {feedbackModal}
      </div>
    );
  }

  return (
    <div className="app-layout">
      <TrialBanner />
      <Navbar
        toggleSidebar={() => setSidebarOpen(!sidebarOpen)}
        onToggleNavMode={toggleNavMode}
      />
      <div className="main-container">
        <Sidebar isOpen={sidebarOpen} toggleSidebar={() => setSidebarOpen(false)} />
        <main className="main-content">
          {backButton}
          {children}
        </main>
      </div>
      {feedbackModal}
    </div>
  );
};
